import { motion } from "framer-motion";
import {
  ShoppingCart,
  Plug,
  Database,
  Handshake,
  CreditCard,
  Code2,
  ArrowUpRight,
} from "lucide-react";

const SERVICES = [
  {
    icon: ShoppingCart,
    title: "B2B E-commerce Platforms",
    description:
      "Custom B2B portals with tiered pricing, quote workflows, contract catalogs and multi-account management.",
    capabilities: ["Quote-to-cash", "Multi-buyer accounts", "Catalog logic"],
    testid: "services-card-b2b-ecommerce",
  },
  {
    icon: Plug,
    title: "API & Integrations",
    description:
      "Robust REST/GraphQL APIs and event-driven pipelines that connect every system in your commerce stack.",
    capabilities: ["REST / GraphQL", "Webhooks", "Event streams"],
    testid: "services-card-api-integrations",
  },
  {
    icon: Database,
    title: "ERP / CRM Connections",
    description:
      "SAP, Oracle, Dynamics, Salesforce, HubSpot — we wire your back-office to your storefront with discipline.",
    capabilities: ["SAP / Oracle", "Salesforce", "Dynamics 365"],
    testid: "services-card-erp-crm",
  },
  {
    icon: Handshake,
    title: "Brokerage & Intermediation",
    description:
      "We connect you with vetted suppliers, distributors and partners across markets — and we close the loop with technology.",
    capabilities: ["Supplier sourcing", "Deal facilitation", "Cross-border partners"],
    testid: "services-card-brokerage",
  },
  {
    icon: CreditCard,
    title: "Payments & Logistics",
    description:
      "Cross-border payments, taxes, carriers and customs flows wired for predictable global fulfillment.",
    capabilities: ["Cross-border", "Tax engines", "Carrier APIs"],
    testid: "services-card-payments-logistics",
  },
  {
    icon: Code2,
    title: "Custom Web & Mobile",
    description:
      "Performance-obsessed web apps and native-grade mobile experiences for partners, ops and field teams.",
    capabilities: ["React / Next", "iOS / Android", "Edge & PWA"],
    testid: "services-card-custom-web-mobile",
  },
];

const Services = () => {
  return (
    <section id="services" data-testid="services-section" className="section-pad relative">
      <div className="container-narrow">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.45 }}
          className="max-w-3xl"
        >
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#E94B4B]">
            — What we do
          </div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#F4EADE]">
            B2B commerce. Built and brokered.
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[#EFE3D6]/75 leading-relaxed">
            From storefronts to back office, from suppliers to settlement — we develop
            the technology and broker the relationships that move global trade.
          </p>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {SERVICES.map((svc, i) => {
            const Icon = svc.icon;
            return (
              <motion.article
                key={svc.title}
                data-testid={svc.testid}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.45, delay: i * 0.05, ease: [0.2, 0.8, 0.2, 1] }}
                className="group relative overflow-hidden rounded-2xl border border-[rgba(244,234,222,0.12)] bg-[rgba(17,16,21,0.7)] p-6 sm:p-7 card-accent hover:border-[rgba(244,234,222,0.25)] transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-[#6B1F47]/25 ring-1 ring-[#6B1F47]/40 text-[#F4EADE]">
                    <Icon className="h-5 w-5" />
                  </div>
                  <ArrowUpRight className="h-5 w-5 text-[#B9AFA6] group-hover:text-[#E94B4B] transition-colors" />
                </div>
                <h3 className="mt-5 font-display text-xl font-semibold text-[#F4EADE]">
                  {svc.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-[#EFE3D6]/75">
                  {svc.description}
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {svc.capabilities.map((c) => (
                    <span
                      key={c}
                      className="inline-flex items-center rounded-full border border-[rgba(244,234,222,0.16)] bg-[rgba(244,234,222,0.03)] px-2.5 py-1 text-[11px] font-mono uppercase tracking-wider text-[#EFE3D6]/80"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
